import { Component, OnDestroy } from '@angular/core';
import { interval, Subject, takeUntil } from 'rxjs';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier, t_DataPoint } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { initCalibrationCabineDataset, initCalibrationJoystickHorizontalDataset, initCalibrationJoystickVerticalDataset, initCalibrationPedalDataset, t_CalibrationDataset } from '../data/calibration-dataNames';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';

@Component({
  selector: 'app-calibration-multi',
  templateUrl: './calibration-multi.component.html',
  styleUrls: ['./calibration-multi.component.scss']
})
export class CalibrationMultiComponent implements OnDestroy{

  // CABINE
  public cabineLoading: boolean = false;
  public cabineRunning: boolean = false;
  public cabineEndpos: string = '';
  public cabineSuccess: boolean = false;
  public clicked: boolean = false;
  public secondLow: boolean = false;

  // FINGERTIP-JOYSTICK
  public joystickLoading: boolean = false;
  public joystickRunning: boolean = false;
  public joystickSuccess: boolean = false;
  public joystickDirection: string = '';
  public joystickUp: boolean = false;
  public joystickLeft: boolean = false;
  
  // PEDAL
  public pedalLoading: boolean = false;
  public pedalRunning: boolean = false;
  public pedalSuccess: boolean = false;
  public pedalInfo: boolean = false;
  
  
  interval: any;
  public calibrationDataset: t_CalibrationDataset = {dataPoints: [{generalName: 'Platzhalter', dataName: { name: 'Platzhalter'}}]};
  public queryFrequency: number = 1000;
  public pollingFrequency: number;
  private destroyed: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(public gatewayConnectionService: GatewayConnectionService, private didConfigService: DidConfigService, private dataRetrieval: DidDataRetrievalService,) {
    if (this.queryFrequency <= 65) {
      this.pollingFrequency = 50;
    } else {
      this.pollingFrequency = parseFloat((this.queryFrequency * 0.75).toFixed(0));
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelRequests();
  }

  private cancelRequests(): Promise<void> {
    return new Promise((resolve) => {
      this.dataRetrieval.stopSendQueryAliveState();
      this.dataRetrieval.stopDataQuery();
      this.dataRetrieval.cleanDataPoints();
      this.gatewayConnectionService.checkGatewayAlive().subscribe({
        next: (isAlive) => {
          if(isAlive){
            this.calibrationDataset!.dataPoints.forEach(data => {
              this.didConfigService.removeDidFromQueryList({ecu_id: data.dataName.identifier!.ecu_id, data_id: data.dataName.identifier!.data_id}).subscribe({
                complete: () => {
                  resolve();
                }
              });
            });
          }
        }
      });
    });
  }

  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.gatewayConnectionService.establishConnectionToGateway().then(
        () => {
          resolve();
        },
        () => {
          console.error('ERROR');
        }
      );
    });
  }

  private startCalibrationProcess(initDataset: t_CalibrationDataset): Promise<void> {
    return new Promise((resolve) => {
      this.setupServices().then(() => {
        if(!this.destroyed) {
          this.dataRetrieval.initService();
          this.calibrationDataset = JSON.parse(JSON.stringify(initDataset));
          this.getDataIdentifiersToDataNamesFromConfig();
          this.postDataIdentifierToQueryToBackend().then(() => {
            if(this.destroyed){
              this.ngOnDestroy();
            } else {
              this.dataRetrieval.startDataQuery();
              resolve();
            }
          });
        }
      });
    });
  }

  private getDataIdentifiersToDataNamesFromConfig() {
    this.calibrationDataset!.dataPoints.forEach((calibrationDataPoint) => {
      calibrationDataPoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(calibrationDataPoint.dataName.name);
      calibrationDataPoint.dataName.didInformation = this.didConfigService.getDidInformation(calibrationDataPoint.dataName.identifier);
    });
    this.calibrationDataset!.dataPoints = this.calibrationDataset!.dataPoints
      .map((dataPoint) => dataPoint)
      .filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined);
    if(this.destroyed) {
      this.ngOnDestroy();
    }
  }

  private postDataIdentifierToQueryToBackend(): Promise<void> {
    return new Promise((resolve) => {
      let dataIdentifierList: t_DataIdentifier[] = this.calibrationDataset!.dataPoints
        .map((dataPoint) => dataPoint.dataName.identifier)
        .filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
      this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
        complete: () => {
          resolve();
        },
      });
    });
  }

  private startPeriodicPolling() {
    interval(this.pollingFrequency)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getDataFromBackend();
      });
  }

  private getDataFromBackend() {
    // Get all Dids Values, because did list was Posted, so no other dids are present in the list
    this.dataRetrieval.getAllDidsValues().subscribe((response) => {
      if (response.body != null) {
        this.processRawData(response.body);
      }
    });
  }

  private processRawData(rawData: t_DataPoint[]) {
    rawData.forEach((dataPoint) => {
      if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
        return;
      }
      let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);
      let normalizedValue: number = this.normalizedValue(
        dataPoint.data.value[0],
        this.calibrationDataset!.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
        this.calibrationDataset!.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
      );
      this.calibrationDataset!.dataPoints[indexInDataset].value = normalizedValue;
    });
  }
  
  private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
    let index: number = this.calibrationDataset!.dataPoints.findIndex((datapoint) => {
      if (datapoint.dataName.identifier) {
        return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
      } else {
        return false;
      }
    });
    return index;
  }
  
  private normalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value * factor) + offset;
    res = parseFloat(res.toFixed(1));
    return res;
  }


  /** DRIVER PEDAL */

  public startCalibrationPedal() {
    this.pedalLoading = true;
    this.startCalibrationProcess(initCalibrationPedalDataset).then(() => {
      this.startPedalTeaching().then(() => {
        this.pedalRunning = true;
        this.pedalLoading = false;
        this.startPeriodicPolling();
        this.handlePedalCalibrationResponse();
      })
      .catch((error) => {
        console.error(error);
        this.cancelPedal();
      });
    });
    /**
     * set start teaching to 1 (write by id)
     * -> pedalRunning to true, and loading to false
     * listen for Press Pedal down
     * -> when 1 dann pedalInfo true
     * listen for TeachingSuccessful
     * -> when 1 dann pedalInfo false, pedal Successful true
     * dann nach kurzer Zeit um Nachricht zu zeigen pedalSuccess und running auf false
    */
  }

  private startPedalTeaching(): Promise<void> {
    return new Promise((resolve) => {
      let identifier = this.calibrationDataset!.dataPoints[0].dataName.identifier;
      this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 1).subscribe({
        complete: () => {
          resolve();
        },
        error: (error) => {
          console.error(error);
          resolve();
        }
      });
    });
  }

  private handlePedalCalibrationResponse() {
    this.interval = interval(this.pollingFrequency).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkForPedalInstructions();
      this.checkForPedalSuccess();
      if(this.pedalSuccess) {
        setTimeout(() => {
          this.cancelPedal();
        }, 8000);
      }
    });
  }

  private checkForPedalInstructions() {
    let value = this.calibrationDataset!.dataPoints[1].value;
    if(value == 1) {
      this.pedalInfo = true;
    }
  }

  private checkForPedalSuccess() {
    let value = this.calibrationDataset!.dataPoints[2].value;
    if(value == 1) {
      this.pedalInfo = false;
      this.pedalSuccess = true;
    }
  }

  public cancelPedal() {
    this.interval.unsubscribe();
    this.cancelRequests().then(() => {
      this.pedalLoading = false;
      this.pedalRunning = false;
      this.pedalSuccess = false;
      this.pedalInfo = false;
    });
  }

  /** FINGERTIP JOYSTICK */

  startCalibrationJoystick(direction: string) {
    this.joystickLoading = true;
    if(direction == 'up') {
      this.joystickDirection = 'up'; //direction
      /**
       * set start to 1 and then 
       * listen for running and then loading to false and running to true
       * listen for instruction then info true
       * then listen for success then success true, info false
       * after short time success and running false and direction to '' 
       */
      this.startCalibrationProcess(initCalibrationJoystickVerticalDataset).then(() => {
        this.startJoystickTeaching().then(() => {
          this.startPeriodicPolling();
          this.handleJoystickCalibrationResponse();
        })
        .catch((error) => {
          console.error(error);
          this.cancelJoystick(this.joystickDirection);
        });
      });
    }
    if(direction == 'left') {
      this.joystickDirection = 'left'; //direction
      this.startCalibrationProcess(initCalibrationJoystickHorizontalDataset).then(() => {
        this.startJoystickTeaching().then(() => {
          this.startPeriodicPolling();
          this.handleJoystickCalibrationResponse();
        })
        .catch((error) => {
          console.error(error);
          this.cancelJoystick(this.joystickDirection);
        });
      });
    }
  }

  private startJoystickTeaching(): Promise<void> {
    return new Promise((resolve) => {
      let identifier = this.calibrationDataset!.dataPoints[0].dataName.identifier;
      this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 1).subscribe({
        complete: () => {
          resolve();
        },
        error: (error) => {
          console.error(error);
          resolve();
        }
      });
    });
  }

  private handleJoystickCalibrationResponse() {
    this.interval = interval(this.pollingFrequency).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkForJoystickRunning().then(() => {
        this.joystickLoading = false;
        this.joystickRunning = true;
        this.checkForJoystickInstructions();
        this.checkForJoystickSuccess();
        if(this.joystickSuccess) {
          setTimeout(() => {
            this.cancelJoystick(this.joystickDirection);
          }, 8000);
        }
      });
    });
  }

  private checkForJoystickRunning(): Promise<void> {
    return new Promise((resolve) => {
      let value = this.calibrationDataset!.dataPoints[1].value;
      if(value == 1) {
        resolve();
      } else if(value == 0) {
        this.checkForJoystickSuccess().then(() => {
          resolve();
        });
      }
    });
  }

  private checkForJoystickInstructions() {
    let value = this.calibrationDataset!.dataPoints[2].value;
    if(value == 1) {
      if(this.joystickDirection == 'up') {
        this.joystickUp = true;
      } else if(this.joystickDirection == 'left') {
        this.joystickLeft = true;
      }
    }
  }

  private checkForJoystickSuccess(): Promise<void> {
    return new Promise((resolve) => {
      let value = this.calibrationDataset!.dataPoints[3].value;
      if(value == 1) {
        this.joystickSuccess = true;
        if(this.joystickDirection == 'up') {
          this.joystickUp = false;
          resolve();
        } else if(this.joystickDirection == 'left') {
          this.joystickLeft = false;
          resolve();
        }
      }
    });
  }

  public cancelJoystick(direction: string) {
    this.interval.unsubscribe();
    this.joystickDirection = '';
    this.joystickLoading = false;
    this.cancelRequests().then(() => {
      if(direction == 'up') {
        this.joystickUp = false;
        this.joystickRunning = false;
        this.joystickSuccess = false;
      }
      if(direction == 'left') {
        this.joystickLeft = false;
        this.joystickRunning = false;
        this.joystickSuccess = false;
      }
    });
  }


  /** CABINE */

  startCalibrationCabine() {
    /**
     * set start calibration to 1
     *  listen for calibration running
     * listen for move cabine to lower
     * send cabine in lower position as 1
     * listen for move cabine to upper pos
     * send cabine in upper pos as 1
     * listen for cabine in lower pos
     * sent cabine in lower pos as 1
     * listen for success
     */
    this.cabineLoading = true;
    this.startCalibrationProcess(initCalibrationCabineDataset).then(() => {
      this.startCabineTeaching().then(() => {
        this.startPeriodicPolling();
        this.handleCabineCalibrationResponse();
      })
      .catch((error) => {
        console.error(error);
        this.cancelCabine();
      });
    });
  }

  private startCabineTeaching(): Promise <void> {
    return new Promise((resolve) => {
      let identifier = this.calibrationDataset!.dataPoints[0].dataName.identifier;
      this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 1).subscribe({
        complete: () => {
          resolve();
        },
        error: (error) => {
          console.error(error);
          resolve();
        }
      });
    });
  }

  private handleCabineCalibrationResponse() {
    this.interval = interval(this.pollingFrequency).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkForCabineRunning().then(() => {
        this.cabineLoading = false;
        this.cabineRunning = true;
        this.checkForCabineInstructions();
        this.checkForCabineSuccess();
        if(this.cabineSuccess) {
          setTimeout(() => {
            this.cancelCabine();
          }, 8000);
        }
      });
    });
  }

  private checkForCabineRunning(): Promise<void> {
    return new Promise((resolve) => {
      let value = this.calibrationDataset!.dataPoints[1].value;
      if(value == 1) {
        resolve();
      } else if(value == 0) {
        this.checkForCabineSuccess().then(() => {
          resolve();
        });
      }
    });
  }

  private checkForCabineInstructions() {
    let lowerValue = this.calibrationDataset!.dataPoints[2].value;
    let upperValue = this.calibrationDataset!.dataPoints[4].value;
    let newEndpos: 'lower' | 'upper' | null = null;

    if(lowerValue == 1 && upperValue == 1) {
      return;
    }
    if(lowerValue == 1){
      newEndpos = 'lower';
    }
    if(upperValue == 1) {
      newEndpos = 'upper';
    }
    if(newEndpos) {
      if(this.cabineEndpos && this.cabineEndpos !== newEndpos) {
        this.clicked = false; 
      }
      this.cabineEndpos = newEndpos;
    }
  }

  private checkForCabineSuccess(): Promise<void> {
    return new Promise((resolve) => {
      let value = this.calibrationDataset!.dataPoints[6].value;
      if(value == 1) {
        this.cabineEndpos = '';
        this.cabineSuccess = true;
        resolve();
      }
    });
  }

  public confirm() {
    // first lower than upper than lower again
    this.clicked = true;
    if(this.cabineEndpos == 'upper') {
      this.setCabineInUpperPos();
    }
    if(this.cabineEndpos == 'lower') {
      this.setCabineInLowerPos();
    }   
  }

  private setCabineInUpperPos() {
    let identifier = this.calibrationDataset!.dataPoints[5].dataName.identifier;
    this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 1).subscribe({
      complete: () => {
        this.secondLow = true;
      }
    });
  }

  private setCabineInLowerPos() {
    let identifier = this.calibrationDataset!.dataPoints[3].dataName.identifier;
    this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 1).subscribe({
      complete: () => {}
    });
  }

  private stopCabineTeaching(): Promise <void> {
    return new Promise((resolve) => {
      let identifier = this.calibrationDataset!.dataPoints[0].dataName.identifier;
      this.didConfigService.writeDataByID(identifier!.ecu_id, identifier!.data_id, 0).subscribe({
        complete: () => {
          resolve();
        }
      });
    });
  }

  cancelCabine() {
    this.interval.unsubscribe();
    this.cancelRequests().then(() => {
      this.stopCabineTeaching();
      this.cabineEndpos = '';
      this.cabineRunning = false;
      this.cabineSuccess = false;
      this.cabineLoading = false;
      this.secondLow = false;
    });
  }

}
